// ** React Imports
import { useState, useEffect, ReactElement } from 'react'

// ** Next Import

// ** MUI Imports
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import { DataGrid, GridRowId } from '@mui/x-data-grid'

// ** Icons Imports
import Send from 'mdi-material-ui/Send'
import Check from 'mdi-material-ui/Check'
import ChartPie from 'mdi-material-ui/ChartPie'
import ArrowDown from 'mdi-material-ui/ArrowDown'
import InformationOutline from 'mdi-material-ui/InformationOutline'
import ContentSaveOutline from 'mdi-material-ui/ContentSaveOutline'

// ** Store & Actions Imports
import { useDispatch, useSelector } from 'react-redux'
import { fetchAllAvailableChannels } from 'src/store/apps/channel'
import { useLocation, useNavigate } from 'react-router-dom';

// ** Types Imports
import { RootState, AppDispatch } from 'src/store'
import { ThemeColor } from 'src/@core/layouts/types'
import { ChannelType, channelDefault } from 'src/types/apps/channelTypes'

// ** Custom Components Imports
import CustomAvatar from 'src/@core/components/mui/avatar'
import TableHeader from 'src/views/apps/channel/list/TableHeader'
import ChannelAddDialog from 'src/views/apps/channel/dialogs/ChannelAddDialog'
import PostContentDialog from 'src/views/apps/channel/dialogs/PostContentDialog'

// ** Third Party Styles Imports
import 'react-datepicker/dist/react-datepicker.css'

interface InvoiceStatusObj {
    [key: string]: {
        color: ThemeColor
        icon: ReactElement
    }
}


interface CellType {
    row: ChannelType
}

// ** Vars

const Channel = () => {
    // ** State
    const [dates, setDates] = useState<Date[]>([])
    const [value, setValue] = useState<string>('')
    const [pageSize, setPageSize] = useState<number>(10)
    const [statusValue, setStatusValue] = useState<string>('')
    //const [endDateRange, setEndDateRange] = useState<DateType>(null)
    const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
    //const [startDateRange, setStartDateRange] = useState<DateType>(new Date())
    const [showAddChannelDialog, setShowAddChannelDialog] = useState<boolean>(false);
    const [medium, setMedium] = useState<number>(9999)
    const [mediumName, setMediumName] = useState<string>('')
    const [channelType, setChannelType] = useState<number>(9999)
    const [channels, setChannels] = useState<ChannelType[]>(channelDefault);
    const [showPostContentDialog, setShowPostContentDialog] = useState<boolean>(false);
    const [availableChannels, setAvailableChannels] = useState<ChannelType[]>(channelDefault);
    const [isDefaultOrgActive, setIsDeafultOrgActive] = useState<boolean>(false); 
    const [activeTab, setActiveTab] = useState('mediumsTab');
    const location = useLocation();
    const navigate = useNavigate();

    // Parse query parameters
    const queryParams = new URLSearchParams(location.search);
    const action = queryParams.get('action');

    useEffect(() => {
        if (action === 'createPost') {
            setShowPostContentDialog(true);
        }
    }, [action]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('modal') === 'add-channel') {
            setShowAddChannelDialog(true);

            const saved = localStorage.getItem('addChannelWizardState');
            if (saved) {
                const wizardState = JSON.parse(saved);
                setMedium(wizardState.medium || 0);
                setMediumName(wizardState.mediumName || '');
                setChannelType(wizardState.channelType || 0);
                setChannels(wizardState.channels || []);
                setActiveTab(wizardState.activeTab || 'mediumsTab');
            }


            params.delete('modal');
            navigate({
                pathname: location.pathname,
                search: params.toString(),
            }, { replace: true });
        }
    }, [location.search, location.pathname, navigate]);

    const defaultColumns = [

        {
            flex: 0.20,
            field: 'client',
            minWidth: 300,
            headerName: 'Name',
            renderCell: ({ row }: CellType) => {
                const { name } = row

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {renderClient(row)}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                variant='body2'
                                sx={{ color: 'text.primary', fontWeight: 500, lineHeight: '22px', letterSpacing: '.1px' }}
                            >
                                {name}
                            </Typography>
                            <Typography noWrap variant='caption'>
                                {row.description}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 125,
            field: 'platform',
            headerName: 'Platform',
            renderCell: ({ row }: CellType) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src={getPlatformIcon(row.mediumName)}
                        alt={`${row.mediumName} icon`}
                        style={{ width: 24, height: 24, marginRight: 8 }}
                    />
                    <Typography variant='body2'>{row.mediumName === "Google" ? "YouTube" : row.mediumName}</Typography>
                </div>
            )
        },
        {
            flex: 0.4,
            minWidth: 125,
            field: 'category',
            headerName: 'Category',
            renderCell: ({ row }: CellType) => <Typography variant='body2'>{row.category}</Typography>
        }
    ]

    // ** Hooks
    const dispatch = useDispatch<AppDispatch>()
    const store = useSelector((state: RootState) => ({ channel :state.channel, lookup : state.lookup }))

    const renderClient = (row: ChannelType) => {
        let imageUrl = row.profileImageUrl ? row.profileImageUrl : `https://graph.facebook.com/v15.0/${row.channelId}/picture?height=40&width=40&access_token=${store.lookup.data.userAccessToken}`
        return <CustomAvatar src={imageUrl} sx={{ mr: 3, width: 34, height: 34 }} />
    }

    useEffect(() =>
    {
        if (store.lookup.data.userOrgChannels) {
            let isDefaultOrgActive = store.lookup.data.userOrgChannels.organizations.some((uc) => uc.active && uc.isDefaultOrg);
            setIsDeafultOrgActive(isDefaultOrgActive);
            if (!isDefaultOrgActive) {
                let currentOrg = store.lookup.data.userOrgChannels.organizations.filter((uc) => uc.active)[0];
                setAvailableChannels(currentOrg.channels);
            }
            else {
                setAvailableChannels(store.channel.data);
            }
        }

    }, [store.channel.data, store.lookup.data.userOrgChannels]);

    useEffect(() => {
        dispatch(
            fetchAllAvailableChannels({
                dates,
                q: value,
                status: statusValue
            })
        )
    }, [dispatch])

    const refreshChannelsData = () => {
        dispatch(
            fetchAllAvailableChannels({
                dates,
                q: value,
                status: statusValue
            }))
    }

    const handleClickAddChannel = () => {
        setShowAddChannelDialog(true);
    }

    const handleFilter = (val: string) => {
        setValue(val)
    }

    const columns = [
        ...defaultColumns
    ]

    const getPlatformIcon = (medium: string): string => {
        switch (medium.toLowerCase()) {
            case 'facebook':
                return 'https://img.icons8.com/?size=48&id=118497&format=png'; // Path to Facebook icon
            case 'instagram':
                return 'https://img.icons8.com/?size=48&id=Xy10Jcu1L2Su&format=png'; // Path to Instagram icon
            case 'tiktok':
                return 'https://img.icons8.com/?size=48&id=vXmXtbBOhqh2&format=png'; // Path to TikTok icon
            case 'pinterest':
                return 'https://img.icons8.com/?size=48&id=63676&format=png'; // Path to Pinterest icon
            case 'google':
                return 'https://img.icons8.com/?size=48&id=19318&format=png'; // Path to YouTube icon
            case 'threads':
                return 'https://img.icons8.com/?size=32&id=AS2a6aA9BwK3&format=png&color=000000'; 
            case 'linkedin':
                return 'https://img.icons8.com/?size=32&id=13930&format=png&color=000000'; 
            case 'reddit':
                return 'https://img.icons8.com/?size=32&id=8weyx4k1jpJy&format=png&color=000000';
            case 'x':
                return 'https://img.icons8.com/?size=32&id=phOKFKYpe00C&format=png&color=000000';
            case 'discord':
                return 'https://img.icons8.com/?size=32&id=30998&format=png&color=000000';
            default:
                return '/images/platforms/default.png'; 
        }
    };

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mt: 2  }}>
                Add Channels
            </Typography>
            <Typography variant="body2" sx={{ mb: 4 }}>
            Add channels from multiple social media platforms
            </Typography>
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <Card>
                    <TableHeader
                        value={value}
                        selectedRows={selectedRows}
                        handleFilter={handleFilter}
                        handleClickAddChannel={handleClickAddChannel}
                        setShowPostContentDialog={setShowPostContentDialog}
                        isDefaultOrgActive={isDefaultOrgActive}
                    />
                    <DataGrid
                        autoHeight
                        pagination
                        rows={availableChannels}
                        columns={columns}
                        pageSize={Number(pageSize)}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{ '& .MuiDataGrid-columnHeaders': { borderRadius: 0 } }}
                        onSelectionModelChange={rows => setSelectedRows(rows)}
                        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                    />
                </Card>
            </Grid>

            <ChannelAddDialog
                show={showAddChannelDialog}
                setShow={setShowAddChannelDialog}
                setMedium={setMedium}
                medium={medium}
                setMediumName={setMediumName}
                mediumName={mediumName}
                setChannels={setChannels}
                channels={channels}
                setChannelType={setChannelType}
                channelType={channelType}
                refreshChannelsData={refreshChannelsData}
                    existingChannels={availableChannels}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
            />
            {showPostContentDialog &&
                <PostContentDialog
                    show={showPostContentDialog}
                    setShow={setShowPostContentDialog}
                />
            }

            </Grid>
        </Container>
    )
}

export default Channel;